import type { Id, protocol } from "@codesandbox/pitcher-client";
import { toSentryContext } from "features/utils/sentry";
import { signal, cleanup, derived } from "impact-react";

import type { EditorStoreProps } from "./types";

type State =
  | {
      status: "IDLE";
    }
  | {
      status: "COMMITTING";
      shellId?: Id;
    }
  | {
      status: "COMMIT_ERROR";
      shellId: Id;
    }
  | {
      status: "DISCARDING";
    }
  | {
      status: "PULLING";
    };

type CommitMessageState = { message: string } & (
  | { status: "IDLE" }
  | {
      status: "GETTING_SUGGESTION";
    }
  | {
      status: "ERROR";
      error: string;
      message: string;
    }
);

export type GitItemWithPathState = protocol.git.GitItem & {
  isChecked: boolean;
};

export function useGit(props: EditorStoreProps) {
  const {
    pitcher,
    editor,
    addStatusNotification,
    addAlertNotification,
    sentry,
  } = props;

  /**
   * STATE
   */
  const state = signal<State>({ status: "IDLE" });
  const commitMessage = signal<CommitMessageState>({
    status: "IDLE",
    message: "",
  });
  const status = signal<protocol.git.GitStatus>({
    branch: editor.type === "branch" ? editor.branch : "",
    changedFiles: {},
    commits: [],
    conflicts: false,
    deletedFiles: [],
    isMerging: false,
    localChanges: false,
    remote: {
      branch: editor.type === "branch" ? editor.branch : "",
      ahead: 0,
      behind: 0,
      head: null,
      safe: true,
    },
    target: {
      branch: editor.type === "branch" ? editor.targetBranchName : "",
      ahead: 0,
      behind: 0,
      head: null,
      safe: true,
    },
  });
  const hasInitialGitStatus = signal(false);
  const targetBranchName = signal(
    editor.type === "branch" ? editor.targetBranchName : "",
  );
  const pathsChanged = signal(updateGitChanges([], status.value));
  const checkedPathsChanged = derived(() =>
    pathsChanged.value
      .filter((item) => item.isChecked)
      .map((item) => item.path),
  );
  const pendingCommit = signal<string | undefined>(undefined);
  const targetDiffStatus = signal<protocol.git.GitDiffStatusResult | undefined>(
    undefined,
  );

  // We immediately go and grab the latest git status, as pitcher
  // only sends notifications on new git statuses
  pitcher.clients.git.getStatus().then((updatedStatus) => {
    status.value = updatedStatus;
    pathsChanged.value = updateGitChanges(pathsChanged.value, updatedStatus);

    // Once we have our initial git status we also go and grab the target git diff
    if (updatedStatus.target.branch) {
      pitcher.clients.git
        .getTargetDiffStatus(updatedStatus.target.branch)
        .then((targetDiffStatusUpdate) => {
          targetDiffStatus.value = targetDiffStatusUpdate;
        });
    }
  });

  /**
   * SUBSCRIPTIONS
   */
  const statusUpdateDisposer = pitcher.clients.git.onStatusUpdated(
    function onStatusUpdated(updatedStatus) {
      // When we have a target and change our head (add new commit), we need to update
      // the diff with our target branch
      if (
        updatedStatus.target.branch &&
        updatedStatus.head !== status.value.head
      ) {
        pitcher.clients.git
          .getTargetDiffStatus(updatedStatus.target.branch)
          .then((targetDiffStatusUpdate) => {
            targetDiffStatus.value = targetDiffStatusUpdate;
          });
      }

      // We do not update our Git Status when pulling and there is no change
      // to the "behind" state, as this will cause UI flicker. We also make sure to
      // check for conflicted state, cause you might pull into a conflicted state
      if (
        state.value.status === "PULLING" &&
        updatedStatus.remote.behind === status.value.remote.behind &&
        updatedStatus.target.behind === status.value.target.behind &&
        !updatedStatus.conflicts
      ) {
        return;
      }

      // When committing the merge commit, we do not want to go out of the committing state until
      // we have an updated "behind", as it would cause flicker in the UI
      if (
        state.value.status === "COMMITTING" &&
        status.value.isMerging &&
        updatedStatus.target.behind === status.value.target.behind
      ) {
        return;
      }

      hasInitialGitStatus.value = true;

      const hasNewCommits =
        updatedStatus.commits[0]?.hash !== status.value.commits[0]?.hash;

      status.value = updatedStatus;
      pathsChanged.value = updateGitChanges(pathsChanged.value, updatedStatus);

      if (hasNewCommits && state.value.status === "COMMITTING") {
        resetStateFrom("COMMITTING");
        pendingCommit.value = undefined;
      } else if (state.value.status === "PULLING") {
        resetStateFrom("PULLING");
      }
    },
  );
  const pullStartedDisposer = pitcher.clients.git.onPullStarted(
    function onPullStarted() {
      // We do not reset pulling state on pullEnded, we rather use
      // the change of commits in the git status update
      state.value = {
        status: "PULLING",
      };
    },
  );

  const commitStartedDisposer = pitcher.clients.git.onCommitStarted(
    function onCommitStarted({ shellId }) {
      state.value = {
        status: "COMMITTING",
        shellId: shellId,
      };
    },
  );
  const commitFinishedDisposer = pitcher.clients.git.onCommitFinished(
    function onCommitFinished({ exitCode }) {
      const isError = exitCode === 1;

      if (isError && state.value.status === "COMMITTING") {
        addAlertNotification({
          message: "Not able to finish the commit, please try again",
        });
        pendingCommit.value = undefined;

        return;
      }
    },
  );

  cleanup(() => {
    statusUpdateDisposer.dispose();
    pullStartedDisposer.dispose();
    commitStartedDisposer.dispose();
    commitFinishedDisposer.dispose();
  });

  /**
   * HELPER FUNCTIONS
   */
  // Ensures we can only go back to IDLE when we are still in the same state
  function resetStateFrom(from: State["status"]) {
    const shouldReset = state.value.status === from;

    if (shouldReset) {
      state.value = { status: "IDLE" };
      return true;
    }

    return false;
  }

  // Used by both pull from remote and from target
  async function pull(target?: string) {
    if (!pitcher.capabilities.git?.pull) {
      return;
    }
    state.value = {
      status: "PULLING",
    };
    try {
      await pitcher.clients.git.pull(target);
      addStatusNotification({
        message: target
          ? `Successfully pulled from ${target} branch`
          : `Successfully pulled from remote`,
        status: "info",
      });
    } catch (error) {
      const errorMessage = String(error);

      addStatusNotification({
        message: errorMessage.includes("Merge conflict")
          ? "There was a merge conflict while pulling from git. Resolve the merge conflict and try again."
          : "Could not finish the operation. Please try again later.",
        status: "error",
      });
      sentry.reportError(
        errorMessage,
        toSentryContext("git", {
          state: "PULLING",
          event: "REJECTED",
        }),
      );
    }
  }

  async function suggestCommitMessage(temperature?: number) {
    const files = checkedPathsChanged.value;

    // Since this is debounced there is a chance there is not more
    // paths available to create a message for or we might be in
    // a state where we should not generate a messsage
    if (!files.length) {
      return;
    }

    commitMessage.value = {
      status: "GETTING_SUGGESTION",
      message: commitMessage.value.message,
    };
    try {
      const result = await pitcher.clients.ai.suggestCommit({
        model: "gpt-3.5-turbo",
        files,
        temperature,
      });
      commitMessage.value = {
        status: "IDLE",
        message: result.commit,
      };
    } catch (error) {
      commitMessage.value = {
        status: "ERROR",
        error: String(error),
        message: commitMessage.value.message,
      };

      throw error;
    }
  }

  return {
    get targetDiffStatus() {
      return targetDiffStatus.value;
    },
    get hasInitialGitStatus() {
      return hasInitialGitStatus.value;
    },
    get state() {
      return state.value;
    },
    get status() {
      return status.value;
    },
    get targetBranchName() {
      return targetBranchName.value;
    },
    get pathsChanged() {
      return pathsChanged.value;
    },
    get checkedPathsChanged() {
      return checkedPathsChanged.value;
    },
    get pendingCommit() {
      return pendingCommit.value;
    },
    get commitMessage() {
      return commitMessage.value;
    },
    setCommitMessage(message: string) {
      commitMessage.value = {
        status: "IDLE",
        message,
      };
    },
    suggestCommitMessage(temperature?: number) {
      return suggestCommitMessage(temperature).catch(() => {
        // We do not care about errors here. The state of the commit
        // message changes and is observed by components
      });
    },
    toggleChangedPath(path: string, checked: boolean) {
      pathsChanged.value = pathsChanged.value.map((change) => {
        if (change.path === path) {
          return {
            ...change,
            isChecked: checked,
          };
        }

        return change;
      });
    },
    async commit() {
      if (!pitcher.capabilities.git?.commit) {
        return;
      }

      state.value = {
        status: "COMMITTING",
      };

      pendingCommit.value = commitMessage.value.message;
      try {
        const push = status.value.remote.behind === 0;
        const { shellId } = await pitcher.clients.git.commit(
          commitMessage.value.message,
          checkedPathsChanged.value,
          push,
        );

        state.value = {
          status: "COMMITTING",
          shellId,
        };
        commitMessage.value = {
          status: "IDLE",
          message: "",
        };

        // The state is still "COMMITTING" as we'll use the status update to detect
        // when a new commit is added to the history
      } catch (error) {
        addAlertNotification({
          message: "Not able to start the commit, please try again",
        });
        resetStateFrom("COMMITTING");
        pendingCommit.value = undefined;
      }
    },
    pullRemote() {
      return pull();
    },
    pullTarget() {
      return pull(targetBranchName.value);
    },
    async discard(pathsToDiscard: string[]) {
      const gitStatus = status.value;

      // If a file was deleted and then discarded, it gets restored.
      const restoredFiles = gitStatus.deletedFiles.filter((item) =>
        pathsToDiscard.includes(item.path),
      );

      // If a file was created but was never tracked, it gets deleted
      // on discard.
      const deletedFiles = Object.values(gitStatus.changedFiles).filter(
        (item) =>
          pathsToDiscard.includes(item.path) &&
          (item.workingTree === "?" || item.index === "?"),
      );

      // Modified files get their changes discarded.
      const discardedFiles = Object.values(gitStatus.changedFiles).filter(
        (item) =>
          pathsToDiscard.includes(item.path) &&
          (item.index === "M" || item.workingTree === "M"),
      );

      // Create notification message.
      const notificationMessages = [];
      if (discardedFiles.length > 0) {
        notificationMessages.push(
          `Discarded changes in ${filesPlural(discardedFiles.length)}.`,
        );
      }
      if (restoredFiles.length > 0) {
        notificationMessages.push(
          `Restored ${filesPlural(restoredFiles.length)}.`,
        );
      }
      if (deletedFiles.length > 0) {
        notificationMessages.push(
          `Deleted ${filesPlural(deletedFiles.length)}.`,
        );
      }

      const discardPaths = discardedFiles
        .concat(restoredFiles)
        .map((f) => f.path);

      if (discardPaths.length > 0 && pitcher.capabilities.git?.discard) {
        state.value = {
          status: "DISCARDING",
        };

        try {
          await pitcher.clients.git.discard(discardPaths);

          if (deletedFiles.length > 0) {
            deletedFiles.forEach(
              ({ fileId }) => fileId && pitcher.clients.fs.deleteNode(fileId),
            );
          }

          addStatusNotification({
            message: notificationMessages.join(" "),
          });
        } catch (error) {
          addStatusNotification({
            message: "Not able to discard changes, please try again",
          });
          sentry.reportError(
            String(error),
            toSentryContext("git", {
              state: "DISCARDING",
              event: "REJECTED",
            }),
          );
        } finally {
          resetStateFrom("DISCARDING");
        }
      }
    },
  };
}

function updateGitChanges(
  changes: GitItemWithPathState[],
  status: protocol.git.GitStatus,
): GitItemWithPathState[] {
  const allChangedPaths = getAllChangedPaths(status);

  return allChangedPaths.map((changedPath) => {
    const existingChange = changes.find(
      (change) => change.path === changedPath.path,
    );

    return {
      ...changedPath,
      isChecked: existingChange?.isChecked ?? true,
    };
  });
}

function getAllChangedPaths(status: protocol.git.GitStatus) {
  const changedFiles = Object.values(status.changedFiles ?? {});
  const conflictedFiles = changedFiles.filter((c) => c.isConflicted);
  const nonConflictedFiles = changedFiles.filter((c) => !c.isConflicted);
  const deletedFiles = status.deletedFiles ?? [];

  return [...conflictedFiles, ...nonConflictedFiles, ...deletedFiles];
}

function filesPlural(filesCount: number) {
  return `${filesCount} ${filesCount === 1 ? "file" : "files"}`;
}
